/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchUsers,
  saveUser,
  clearUserError,
} from "../store/slices/userSlice";
import { fetchRoles } from "../store/slices/roleSlice";
import { fetchEmployees } from "../store/slices/employeeSlice";
import { fetchBranchesByCompanyId } from "../store/slices/branchSlice";
import { fetchDepartments } from "../store/slices/departmentSlice";
import { fetchCompanyByType } from "../store/slices/companySlice"; 
import Layout from "../common/components/Layout";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Users = () => {
  const dispatch = useDispatch();

  // Selectors
  const { list: users = [], status } = useSelector((state) => state.user || {});
  const { list: roles = [] } = useSelector((state) => state.role || {});
  const { list: employees = [] } = useSelector((state) => state.employee || {});
  const { list: branches = [] } = useSelector((state) => state.branch || {});
  const { list: departments = [] } = useSelector((state) => state.department || []);
  const { list: companies = [] } = useSelector((state) => state.companyTypeList || []);

  const localCompanyId = localStorage.getItem("companyId") || "";

  const initialFormState = {
    id: "", name: "", email: "", password: "", roleId: "", employeeId: "",
    companyId: localCompanyId, branchId: "", assignmentType: "both", departmentId: "", isActive: true,
  };

  /* ===================== LOCAL STATE ===================== */
  const [formData, setFormData] = useState(initialFormState);
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  /* ===================== FILTER & PAGINATION STATE ===================== */
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [roleFilter, setRoleFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: "email", direction: "asc" });

  useEffect(() => {
    dispatch(fetchUsers());
    dispatch(fetchRoles());
    dispatch(fetchEmployees());
    dispatch(fetchCompanyByType());
    dispatch(fetchDepartments(localCompanyId));
  }, [dispatch, localCompanyId]);

  useEffect(() => {
    if (formData.companyId) {
      dispatch(fetchBranchesByCompanyId({ companyId: formData.companyId, role: "" }));
      dispatch(fetchDepartments(formData.companyId));
    }
  }, [dispatch, formData.companyId]);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, statusFilter, roleFilter, sortConfig]);

  /* ===================== HELPER FUNCTIONS ===================== */
  const toCamelCase = (str) => {
    if (!str) return "Active";
    const s = str.toString().toLowerCase();
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <i className="bi bi-arrow-down-up ms-2 opacity-25" style={{ fontSize: '0.6rem' }}></i>;
    return sortConfig.direction === "asc" 
      ? <i className="bi bi-sort-down ms-2 text-primary" style={{ fontSize: '0.7rem' }}></i> 
      : <i className="bi bi-sort-up ms-2 text-primary" style={{ fontSize: '0.7rem' }}></i>;
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  /* ===================== LOGIC HANDLERS ===================== */
  const handleOpenAdd = () => {
    dispatch(clearUserError());
    setIsEditMode(false);
    setFormData({ ...initialFormState, companyId: localCompanyId });
    setShowModal(true);
  };

  const handleOpenEdit = (user) => {
    dispatch(clearUserError());
    setIsEditMode(true);
    
    // Ensure lists are fetched for the user's specific company
    const targetCompanyId = user.companyId || user.company?.id || localCompanyId;
    dispatch(fetchDepartments(targetCompanyId));
    dispatch(fetchBranchesByCompanyId({ companyId: targetCompanyId, role: "" }));

    setFormData({
      id: user.id,
      name: user.employeeFirstName ? `${user.employeeFirstName} ${user.employeeLastName}` : "",
      email: user.email || "",
      password: "", 
      roleId: user.roleId || "",
      employeeId: user.employeeId || user.employee?.id || "",
      companyId: targetCompanyId,
      branchId: user.branchId || user.branch?.id || "",
      
      departmentId: user.departmentId || "", // Map ID directly
      isActive: user.isActive,
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    // Mapped exactly to your required JSON payload
    const payload = {
      email: formData.email,
      password: formData.password?.trim() ? formData.password : undefined,
      companyId: formData.companyId,
      branchId: formData.branchId || null,
      employeeId: formData.employeeId || null,
      roleId: formData.roleId,
      departmentId: formData.departmentId || null, // Corrected to use ID
      isActive: formData.isActive,
      isEmailVerified: false
    };

    if (isEditMode) payload.id = formData.id;

    try {
      await dispatch(saveUser(payload)).unwrap();
      toast.success("User saved successfully!");
      setShowModal(false);
      dispatch(fetchUsers());
    } catch (err) {
      toast.error(err || "An error occurred.");
    }
  };

  const handleStatusToggle = (user) => {
    if (user.isActive) {
      setPendingUser(user);
      setShowConfirm(true);
    } else {
      executeStatusUpdate(user);
    }
  };

  const executeStatusUpdate = async (user) => {
    try {
      await dispatch(saveUser({ id: user.id, isActive: !user.isActive })).unwrap();
      toast.success("Status updated!");
      setShowConfirm(false);
      dispatch(fetchUsers());
    } catch (err) {
      toast.error("Failed to update status.");
    }
  };

  /* ===================== FILTER & PAGINATION LOGIC ===================== */
  const processedData = useMemo(() => {
    if (!Array.isArray(users)) return [];
    let items = users.filter((item) => {
      const searchStr = searchTerm.toLowerCase();
      const firstName = item.employeeFirstName || item.employee?.firstName || "";
      const lastName = item.employeeLastName || item.employee?.lastName || "";
      const nameMatch = `${firstName} ${lastName}`.toLowerCase().includes(searchStr);
      const emailMatch = (item.email || "").toLowerCase().includes(searchStr);
      const branchName = item.branchName || item.branch?.branchName || "";
      const branchMatch = branchName.toLowerCase().includes(searchStr);

      const matchesStatus = statusFilter === "All" || (item.isActive ? "Active" : "Inactive") === statusFilter;
      const matchesRole = roleFilter === "All" || item.roleId === roleFilter;
      
      return (nameMatch || emailMatch || branchMatch) && matchesStatus && matchesRole;
    });

    return items.sort((a, b) => {
      const valA = String(a[sortConfig.key] || "").toLowerCase();
      const valB = String(b[sortConfig.key] || "").toLowerCase();
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [users, searchTerm, statusFilter, roleFilter, sortConfig]);

  const totalItems = processedData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentData = processedData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const getPaginationGroup = () => {
    const totalDisplayed = 5; 
    let start = Math.max(currentPage - Math.floor(totalDisplayed / 2), 1);
    let end = Math.min(start + totalDisplayed - 1, totalPages);
    if (end === totalPages) start = Math.max(end - totalDisplayed + 1, 1);
    const pages = [];
    for (let i = start; i <= end; i++) if (i > 0) pages.push(i);
    return pages;
  };

  const inputStyle = { border: "1px solid rgba(0,0,0,0.1)", borderRadius: "8px", padding: "10px 14px", fontSize: "0.8rem", backgroundColor: "rgba(255,255,255,0.7)", backdropFilter: "blur(5px)", outline: "none" };

  return (
    <Layout>
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="container-fluid py-4 flex-grow-1 px-lg-5 w-100">
        <style>{`
          .custom-table-header th { background-color: #1e293b !important; color: #ffffff !important; cursor: pointer; font-size: 0.7rem !important; font-weight: 700 !important; letter-spacing: 0.05em !important; text-transform: uppercase !important; }
          .page-link-custom { width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; border-radius: 8px !important; margin: 0 3px; font-size: 0.75rem; font-weight: 600; color: #64748b; cursor: pointer; background: transparent; transition: all 0.2s; }
          .page-link-custom.active { background: #0f172a !important; color: #fff !important; }
          .page-link-custom.disabled { opacity: 0.3; cursor: not-allowed; }
          .pagination-container { background: rgba(255, 255, 255, 0.4); border-top: 1px solid rgba(0,0,0,0.05); padding: 1rem 1.5rem; }
        `}</style>

        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="fw-bold text-dark m-0" style={{ letterSpacing: "-1.5px" }}>User Management</h1>
            <p className="text-muted small mb-0">Control system access, roles, and deployment types</p>
          </div>
          <button className="btn btn-primary px-4 border-0" style={{ borderRadius: "8px", backgroundColor: "#0f172a", fontWeight: "600", fontSize: '0.85rem' }} onClick={handleOpenAdd}>
            <i className="bi bi-plus-lg me-2"></i>Add New
          </button>
        </div>

        <div className="row g-2 mb-4">
          <div className="col-md-6">
            <input type="text" className="form-control" placeholder="Search by name, email, or branch..." style={inputStyle} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
          <div className="col-md-3">
            <select className="form-select" style={inputStyle} value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="All">All Roles</option>
              {roles.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
            </select>
          </div>
          <div className="col-md-3">
            <select className="form-select" style={inputStyle} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="All">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div style={{ backgroundColor: "rgba(255, 255, 255, 0.6)", backdropFilter: "blur(12px)", borderRadius: "16px", border: "1px solid rgba(255, 255, 255, 0.3)", boxShadow: "0 10px 30px rgba(0,0,0,0.05)", overflow: "hidden" }}>
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="custom-table-header">
                <tr>
                  {[
                    { label: "USER / EMPLOYEE", key: "email" },
                    { label: "EMAIL", key: "email" },
                    { label: "BRANCH", key: "branchName" },
                    { label: "STATUS", key: "isActive" },
                    { label: "ACTIONS", key: null }
                  ].map((col, idx) => (
                    <th key={idx} className={`${idx === 0 ? 'ps-4' : ''} py-3 border-0`}
                      style={{ cursor: col.key ? 'pointer' : 'default' }}
                      onClick={() => col.key && requestSort(col.key)}>
                      {col.label} {col.key && getSortIcon(col.key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody style={{ borderTop: "0" }}>
                {currentData.map((user) => (
                  <tr key={user.id} className="align-middle">
                    <td className="ps-4 py-3">
                      {user.employee || user.employeeFirstName ? (
                        <div>
                          <div className="fw-bold text-dark" style={{ fontSize: '0.85rem' }}>
                            {user.employee?.firstName || user.employeeFirstName} {user.employee?.lastName || user.employeeLastName}
                          </div>
                          <div className="text-muted small" style={{ fontSize: '0.7rem' }}>
                            {roles.find(r => r.id === user.roleId)?.title || "User"}
                          </div>
                        </div>
                      ) : (
                        <div className="text-muted italic small">System Account (Unlinked)</div>
                      )}
                    </td>
                    <td style={{ fontSize: '0.8rem', color: '#334155' }}>{user.email}</td>
                    <td style={{ fontSize: '0.8rem', color: '#334155' }}>{user.branch?.branchName || user.branchName || "Main HQ"}</td>
                    <td>
                      <span className={`badge rounded-pill ${user.isActive ? "bg-success bg-opacity-10 text-success border border-success" : "bg-danger bg-opacity-10 text-danger border border-danger"} border-opacity-25`} style={{ fontSize: "0.65rem", padding: '6px 12px' }}>
                        {toCamelCase(user.isActive ? "Active" : "Inactive")}
                      </span>
                    </td>
                    <td className="pe-4 text-end">
                      <div className="d-flex justify-content-end gap-2">
                        <button className="btn btn-sm btn-outline-primary px-3 fw-bold" style={{ borderRadius: '6px', fontSize: '0.75rem' }} onClick={() => handleOpenEdit(user)}>Edit</button>
                        <button className={`btn btn-sm ${user.isActive ? "btn-outline-danger" : "btn-outline-success"} px-3 fw-bold`} style={{ borderRadius: '6px', fontSize: '0.75rem', minWidth: '95px' }} onClick={() => handleStatusToggle(user)}>
                          {user.isActive ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination-container d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Show</span>
              <select className="form-select form-select-sm" style={{ width: 'auto', borderRadius: '6px' }} value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                {[10, 25, 50].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              <span className="text-muted small">of <strong>{totalItems}</strong> entries</span>
            </div>
            <nav className="d-flex align-items-center">
              <button className={`page-link-custom ${currentPage === 1 ? 'disabled text-muted' : ''}`} onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}><i className="bi bi-chevron-left"></i></button>
              {totalPages > 1 && <button className={`page-link-custom ${currentPage === 1 ? 'active' : ''}`} onClick={() => setCurrentPage(1)}>1</button>}
              {getPaginationGroup().map(p => p !== 1 && p !== totalPages && (
                <button key={p} className={`page-link-custom ${currentPage === p ? 'active' : ''}`} onClick={() => setCurrentPage(p)}>{p}</button>
              ))}
              <button className={`page-link-custom ${currentPage === totalPages ? 'disabled text-muted' : ''}`} onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}><i className="bi bi-chevron-right"></i></button>
            </nav>
          </div>
        </div>
      </div>

      {showConfirm && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(15,23,42,0.6)", backdropFilter: "blur(4px)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 3000 }}>
          <div className="bg-white p-4 rounded-4 shadow-lg text-center" style={{ width: "400px" }}>
            <i className="bi bi-exclamation-triangle text-danger" style={{ fontSize: "3rem" }}></i>
            <h5 className="fw-bold mt-3">Confirm Deactivation</h5>
            <p className="text-muted small">Are you sure you want to deactivate <strong>{pendingUser?.email}</strong>?</p>
            <div className="d-flex gap-2 mt-4">
              <button className="btn btn-light w-100 fw-bold" style={{ borderRadius: '8px' }} onClick={() => setShowConfirm(false)}>Cancel</button>
              <button className="btn btn-danger w-100 fw-bold" style={{ borderRadius: '8px' }} onClick={() => executeStatusUpdate(pendingUser)}>Yes, Deactivate</button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(15,23,42,0.4)", backdropFilter: "blur(4px)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 2000 }}>
          <div className="bg-white p-4 rounded-4 shadow-lg border-0" style={{ width: "650px", maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="fw-bold m-0 text-uppercase" style={{ fontSize: '1rem', letterSpacing: '1px' }}>{isEditMode ? "Edit" : "Create"} User Account</h5>
              <button className="btn-close" onClick={() => setShowModal(false)}></button>
            </div>
            <form onSubmit={handleSave}>
              <div className="row g-3">
                <div className="col-md-12">
                  <label className="small fw-bold mb-1 text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>Email Address</label>
                  <input type="email" className="form-control" required style={{ border: "1px solid #ddd", borderRadius: "8px" }} value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                </div>
                <div className="col-md-6">
                  <label className="small fw-bold mb-1 text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>Password {isEditMode && "(Leave blank to keep)"}</label>
                  <input type="password" className="form-control" required={!isEditMode} style={{ border: "1px solid #ddd", borderRadius: "8px" }} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} />
                </div>
                <div className="col-md-6">
                  <label className="small fw-bold mb-1 text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>Assigning Company</label>
                  <select className="form-select" style={{ border: "1px solid #ddd", borderRadius: "8px" }} value={formData.companyId} onChange={(e) => setFormData({ ...formData, companyId: e.target.value })} required>
                    <option value="">Select Company</option>
                    {companies.map((c) => <option key={c.id} value={c.id}>{c.companyName}</option>)}
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="small fw-bold mb-1 text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>System Role</label>
                  <select className="form-select" style={{ border: "1px solid #ddd", borderRadius: "8px" }} value={formData.roleId} onChange={(e) => setFormData({ ...formData, roleId: e.target.value })} required>
                    <option value="">Select Role</option>
                    {roles.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="small fw-bold mb-1 text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>Assign to Branch</label>
                  <select className="form-select" style={{ border: "1px solid #ddd", borderRadius: "8px" }} value={formData.branchId} onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}>
                    <option value="">None (Main HQ)</option>
                    {branches.map((b) => <option key={b.id} value={b.id}>{b.branchName}</option>)}
                  </select>
                </div>
                <div className="col-md-12">
                  <label className="small fw-bold mb-1 text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>Link to Employee</label>
                  <select className="form-select" style={{ border: "1px solid #ddd", borderRadius: "8px" }} value={formData.employeeId} onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}>
                    <option value="">No Link</option>
                    {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.firstName} {emp.lastName}</option>)}
                  </select>
                </div>
                <div className="col-12">
                  <label className="small fw-bold mb-1 text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>Department</label>
                  <select className="form-select" style={{ border: "1px solid #ddd", borderRadius: "8px" }} value={formData.departmentId} onChange={(e) => setFormData({ ...formData, departmentId: e.target.value })}>
                    <option value="">Select Dept</option>
                    {departments.map((d) => <option key={d.id} value={d.id}>{d.departmentName}</option>)}
                  </select>
                </div>
                <div className="col-12">
                  <div className="form-check form-switch mt-2">
                    <input className="form-check-input" type="checkbox" checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />
                    <label className="form-check-label small fw-bold text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>Active Status</label>
                  </div>
                </div>
              </div>
              <div className="d-flex gap-2 mt-4 pt-3 border-top">
                <button type="button" className="btn btn-light w-100 fw-bold" style={{ borderRadius: '8px' }} onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary w-100 fw-bold" style={{ backgroundColor: "#0f172a", borderRadius: '8px' }} disabled={status === "loading"}>
                  {status === "loading" ? "Processing..." : "Confirm & Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Users;